import { Link } from 'react-router-dom';
import { Star } from 'lucide-react';
import { useWatchlist } from '../lib/watchlist';
import { LivePrice, Skeleton } from './ui';
import type { WatchItem } from '../lib/types';

/** Star any game or prop onto the watchlist. */
export function WatchStar({
  kind,
  refId,
  gameId,
  label,
  size = 16,
}: {
  kind: WatchItem['kind'];
  refId: string;
  gameId?: string | null;
  label: string;
  size?: number;
}) {
  const { isWatched, toggle } = useWatchlist();
  const on = isWatched(kind, refId);
  return (
    <button
      type="button"
      className={`star-btn ${on ? 'on' : ''}`}
      aria-pressed={on}
      aria-label={on ? `Remove ${label} from watchlist` : `Add ${label} to watchlist`}
      title={on ? 'On your watchlist' : 'Add to watchlist'}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        void toggle({ kind, ref: refId, gameId, label });
      }}
    >
      <Star size={size} strokeWidth={1.75} fill={on ? 'currentColor' : 'none'} />
    </button>
  );
}

function Row({ item }: { item: WatchItem }) {
  const q = item.quote;
  const body = (
    <>
      <div className="watch-row-main">
        <div className="watch-title">{q?.title ?? item.label}</div>
        <div className="watch-sub">{q?.sub ?? 'No market right now'}</div>
      </div>
      <div className="watch-row-side">
        {q ? (
          <>
            <LivePrice value={q.odds ?? q.price} change={q.odds ? null : q.change} />
            {q.note ? (
              <span className={`watch-note ${q.noteTone === 'up' ? 'text-up' : q.noteTone === 'down' ? 'text-down' : q.noteTone === 'brand' ? 'text-brand' : 'faint'}`}>
                {q.note}
              </span>
            ) : q.odds && q.change != null ? (
              <span className={`watch-note ${q.change > 0 ? 'text-up' : q.change < 0 ? 'text-down' : 'faint'}`}>
                {q.change > 0 ? '▲' : q.change < 0 ? '▼' : '—'} {Math.abs(q.change)}
              </span>
            ) : null}
          </>
        ) : (
          <span className="faint">—</span>
        )}
      </div>
      <WatchStar kind={item.kind} refId={item.ref} gameId={item.gameId} label={item.label} size={15} />
    </>
  );
  return item.gameId ? (
    <Link to={`/game/${item.gameId}`} className="watch-row">{body}</Link>
  ) : (
    <Link to="/props" className="watch-row">{body}</Link>
  );
}

/** The watchlist itself: a right panel on desktop, a bottom sheet's body on phones. */
export function WatchlistBody() {
  const { items, loading } = useWatchlist();
  if (loading) {
    return (
      <div className="watch-list">
        {Array.from({ length: 3 }, (_, i) => <Skeleton key={i} style={{ height: 44, margin: '8px 0' }} />)}
      </div>
    );
  }
  if (!items.length) {
    return (
      <div className="watch-empty">
        <Star size={20} strokeWidth={1.75} />
        <p>Star a game or a prop and it shows up here with its live price.</p>
      </div>
    );
  }
  return (
    <div className="watch-list">
      {items.map((i) => <Row key={`${i.kind}:${i.ref}`} item={i} />)}
    </div>
  );
}

export function WatchlistPanel() {
  const { items } = useWatchlist();
  return (
    <aside className="watch-panel" aria-label="Watchlist">
      <header className="watch-head">
        <h2>Watchlist</h2>
        <span className="faint num">{items.length}</span>
      </header>
      <WatchlistBody />
    </aside>
  );
}
